import React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {useReplaceCategoryMutation, useLazyGetUploadUrlQuery} from 'api';
import {getImageURL} from '../../../utils/imageUrl';
import CategoryForm from './CategoryForm';
import {categoryValidationSchema} from '../utils';

function CategoryEditIconButton({category, parent}) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [replaceCategory] = useReplaceCategoryMutation();
  const [getUploadUrl] = useLazyGetUploadUrlQuery();
  const {enqueueSnackbar} = useSnackbar();

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleOpen = () => {
    setIsModalOpen(true);
  };

  const {
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    formState: {errors, isSubmitting},
  } = useForm({
    resolver: yupResolver(categoryValidationSchema),
    defaultValues: {
      _id: category._id,
      name: category.name,
      slug: category.slug,
      icon: getImageURL(category.icon),
      attributes: category.attributes,
    },
  });
  const onSubmit = async values => {
    try {
      let uploadResult = category.icon;
      if (values.icon.file) {
        const formData = new FormData();
        formData.append(`file`, values.icon.file);
        const {url} = await getUploadUrl().unwrap();
        const response = await fetch(url, {
          method: 'POST',
          body: formData,
        });
        if (!response.ok) {
          throw new Error('Failed to upload icon.');
        }
        const imageObj = await response.json();
        uploadResult = imageObj.result.id;
      }
      const newObj = {
        ...values,
        icon: uploadResult,
        slug: `${values.slug}`,
        parent,
      };
      await replaceCategory(newObj).unwrap();
      reset();
      enqueueSnackbar('Category created successfully', {
        variant: 'success',
      });
      setIsModalOpen(false);
    } catch (e) {
      const message = e.data.status || undefined;
      enqueueSnackbar(message || 'Failed to create category', {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Dialog open={isModalOpen}>
        <DialogTitle>Update Category</DialogTitle>
        <DialogContent dividers>
          <CategoryForm setValue={setValue} control={control} watch={watch} errors={errors} />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={isSubmitting} autoFocus onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={isSubmitting}
            color="success"
            endIcon={isSubmitting ? <CircularProgress size={16} /> : null}
            onClick={handleSubmit(onSubmit)}
          >
            update
          </Button>
        </DialogActions>
      </Dialog>
      <IconButton disabled={isSubmitting} onClick={handleOpen} aria-label="edit" color="primary">
        <EditIcon />
      </IconButton>
    </>
  );
}

export default CategoryEditIconButton;
